import React from 'react';
import SEO from '../components/SEO';
import './FeelOMeter.css';

const AndroidIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.523 15.341a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zm-9.219 0a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zM17.9 10l1.448-2.508a.302.302 0 00-.11-.412.302.302 0 00-.412.11l-1.468 2.542A8.965 8.965 0 0012 8.545c-1.97 0-3.79.635-5.258 1.687L5.274 7.19a.302.302 0 00-.412-.11.302.302 0 00-.11.412L6.2 10c-2.133 1.454-3.548 3.795-3.548 6.453 0 .303.017.602.048.898h18.6c.031-.296.048-.595.048-.898C21.348 13.795 19.933 11.454 17.9 10z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const features = [
  { icon: '🎯', title: 'AI-Powered Mood Tracking', desc: 'Track your emotions daily with precision. Our smart system helps you recognize patterns and discover triggers over time.' },
  { icon: '🌀', title: 'Feelings Wheel', desc: 'Navigate through emotions using an intuitive feelings wheel built on Dr. Robert Plutchik\'s framework — from core emotions to specific feelings like "Lonely" or "Overwhelmed."' },
  { icon: '💡', title: 'Personalized Reflections', desc: 'Receive tailored suggestions and reflections that guide you to understand and manage your emotional health better.' },
  { icon: '🧘', title: 'Guided Activities', desc: 'Get step-by-step guided exercises for immediate stress relief or long-term emotional well-being.' },
  { icon: '🎵', title: 'Voice-Guided Support', desc: 'Relax with audio-guided breathing exercises and meditations designed to help you regain calm and focus.' },
  { icon: '🔒', title: 'Privacy First', desc: 'All your data stays private on your device. We never share your information without your permission.' },
];

const screenshots = [
  {
    src: '/images/feel-o-meter/feel-o-meter-screenshot-mood-checkin.jpeg',
    alt: 'Feel-O-Meter – mood check-in screen',
  },
  {
    src: '/images/feel-o-meter/feel-o-meter-screenshot-feelings-wheel.jpeg',
    alt: 'Feel-O-Meter – feelings wheel screen',
  },
  {
    src: '/images/feel-o-meter/feel-o-meter-screenshot-insights-analytics.jpeg',
    alt: 'Feel-O-Meter – insights and analytics screen',
  },
  {
    src: '/images/feel-o-meter/feel-o-meter-screenshot-guided-activities.jpeg',
    alt: 'Feel-O-Meter – guided activities screen',
  },
  {
    src: '/images/feel-o-meter/feel-o-meter-screenshot-voice-support.jpeg',
    alt: 'Feel-O-Meter – voice-guided support screen',
  },
];

const FeelOMeter = () => (
  <div className="product-page">
    <SEO
      title="Feel-O-Meter: AI Mood Tracker — Master Your Emotions | Appshow"
      description="Track your mood with AI-powered insights. Explore the feelings wheel, get personalized reflections, and nurture your mental health with guided exercises. Privacy-first design."
      keywords="mood tracker, AI mood tracking, emotional wellness, mental health app, feelings wheel, mood journal, anxiety tracker"
      url="/feel-o-meter-ai-mood-tracker"
    />

    <section className="pp-hero">
      <div className="container pp-hero-inner">
        <div className="pp-hero-content">
        <div className="pp-hero-meta">
          <span className="badge-live">Live</span>
          <span className="pp-category">Lifestyle</span>
        </div>
        <h1 className="pp-title">Feel-O-Meter</h1>
        <p className="pp-subtitle">AI-Powered Mood Tracking</p>
        <p className="pp-description">
          Master Your Emotions. Build Resilience. Live Fully. Feel-O-Meter is your personal emotional wellness app
          designed to help you track feelings, uncover patterns, and nurture mental health with AI-powered insights
          and guided support.
        </p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.appshow.feelometer" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon />
            <span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/us/app/feel-o-meter-ai-mood-tracker/id6752644814" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon />
            <span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
        </div>
        <p className="pp-note">Free · iOS 15.1+ · Android · 5.0★ Rating · Data stays on your device</p>
        </div>
        <div className="pp-hero-preview">
          <img
            src="/images/feel-o-meter/feel-o-meter-ai-mood-tracker-app-preview.png"
            alt="Feel-O-Meter AI mood tracker app preview – emotional wellness journal"
            className="pp-preview-img"
          />
        </div>
      </div>
    </section>

    <section className="pp-screenshots">
      <div className="container">
        <h2 className="pp-section-title">Screenshots</h2>
        <div className="screenshots-row">
          {screenshots.map((s, i) => (
            <div className="phone-frame" key={i}>
              <img src={s.src} alt={s.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="pp-features">
      <div className="container">
        <h2 className="pp-section-title">Why Feel-O-Meter?</h2>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="pp-cta">
      <div className="container">
        <h2>Start your emotional wellness journey today</h2>
        <p>Free to download. Your data stays private on your device.</p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.appshow.feelometer" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon /><span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/us/app/feel-o-meter-ai-mood-tracker/id6752644814" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon /><span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default FeelOMeter;
